import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, clearAuth, getStoredUser, getToken, setStoredUser, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [visibility, setVisibility] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 29.1 — SE attendance-first gate. `undefined` = not checked yet,
  // `null` = checked, no punch-in today, object = today's attendance row.
  const [attendanceToday, setAttendanceToday] = useState(undefined);

  // Fetched for SE/ASM/ZSM — SE is gated everywhere; ASM/ZSM are only
  // gated on the specific Route/Beat/TA-DA screens (see ProtectedRoute's
  // `strictRoles` prop), so their real punch-in status still needs to be
  // known even though most of their screens don't check it.
  const checkAttendance = useCallback(async (forUser) => {
    if (!forUser || !["SE", "ASM", "ZSM"].includes(forUser.role_code)) {
      setAttendanceToday(null);
      return;
    }
    try {
      const { data } = await api.get("/attendance/today");
      setAttendanceToday(data || null);
    } catch (_e) {
      setAttendanceToday(null);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setVisibility(null);
      setAttendanceToday(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me", { skipAuthRedirect: true });
      setUser(data.user);
      setStoredUser(data.user);
      setVisibility(data.visibility);
      await checkAttendance(data.user);
      return data;
    } catch (_e) {
      clearAuth();
      setUser(null);
      setVisibility(null);
      setAttendanceToday(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [checkAttendance]);

  const refreshAttendance = useCallback(() => checkAttendance(user), [checkAttendance, user]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async ({ identifier, password, loginType, rememberMe = false }) => {
    const { data } = await api.post(
      "/auth/login",
      { identifier, password, login_type: loginType, remember_me: rememberMe },
      { skipAuthRedirect: true }
    );
    setToken(data.access_token);
    setUser(data.user);
    setStoredUser(data.user);
    await checkAttendance(data.user);
    return data;
  }, [checkAttendance]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", null, { skipAuthRedirect: true });
    } catch (_e) {
      /* best-effort */
    }
    clearAuth();
    setUser(null);
    setVisibility(null);
    setAttendanceToday(null);
  }, []);

  const markPasswordChanged = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, must_change_password: false };
      setStoredUser(next);
      return next;
    });
  }, []);

  const hasPermission = useCallback(
    (perm) => Array.isArray(user?.permissions) && user.permissions.includes(perm),
    [user]
  );
  const hasAnyPermission = useCallback(
    (perms) => Array.isArray(user?.permissions) && perms.some((p) => user.permissions.includes(p)),
    [user]
  );
  // Phase 20 — workflow permissions (19-code layer, per-user).
  const hasWorkflowPerm = useCallback(
    (code) => Array.isArray(user?.workflow_permissions) && user.workflow_permissions.includes(code),
    [user]
  );

  // ASM and ZSM can use either layout: the mobile "field app" (visits, orders,
  // attendance, beat, TA/DA) or the full browser/office view (dashboard, every
  // screen they have access to, reports). They pick once per device; the
  // choice is remembered and can be switched from the menu at any time.
  // SE and DISTRIBUTOR are fixed to the field app / distributor portal.
  const canSwitchView = user?.role_code === "ASM" || user?.role_code === "ZSM";
  const [modeTick, setModeTick] = useState(0);
  const viewMode = useMemo(() => {
    if (!user?.id || !canSwitchView) return null;
    try {
      const v = localStorage.getItem(`tulsi_view_mode:${user.id}`);
      return v === "field" || v === "browser" ? v : null;
    } catch { return null; }
    // modeTick re-reads storage after setViewMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, canSwitchView, modeTick]);
  const setViewMode = useCallback((mode) => {
    if (!user?.id) return;
    try { localStorage.setItem(`tulsi_view_mode:${user.id}`, mode); } catch { /* storage blocked */ }
    setModeTick((n) => n + 1);
  }, [user?.id]);
  const needsViewChoice = canSwitchView && !viewMode;
  const isField = useMemo(() => {
    const role = user?.role_code;
    if (role === "SE" || role === "DISTRIBUTOR") return true;
    if (canSwitchView) return viewMode === "field";
    return false;
  }, [user, canSwitchView, viewMode]);

  const value = useMemo(
    () => ({
      user,
      visibility,
      loading,
      login,
      logout,
      refreshMe,
      markPasswordChanged,
      hasPermission,
      hasAnyPermission,
      hasWorkflowPerm,
      isField,
      canSwitchView,
      viewMode,
      setViewMode,
      needsViewChoice,
      attendanceToday,
      refreshAttendance,
    }),
    [user, visibility, loading, login, logout, refreshMe, markPasswordChanged, hasPermission, hasAnyPermission, hasWorkflowPerm, isField, canSwitchView, viewMode, setViewMode, needsViewChoice, attendanceToday, refreshAttendance]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// `strictRoles` — which roles this particular route gates on attendance
// when `requireAttendance` is true (the default `["SE"]` matches the
// blanket rule; Route/Beat/TA-DA additionally gate ASM and ZSM, and
// Primary Order / the More menu opt out entirely via requireAttendance=false).
export function ProtectedRoute({ children, requireChangedPassword = true, requireAttendance = true, strictRoles = ["SE"] }) {
  const { user, loading, attendanceToday } = useAuth();
  const location = useLocation();
  const gated = requireAttendance && strictRoles.includes(user?.role_code);
  const attendancePunchedIn = !gated || !!attendanceToday?.check_in_ts;
  // A user resolving a past pending Day End is sent to /tada?date=YYYY-MM-DD
  // to file that day's claim — that claim is the very thing unblocking
  // today's punch-in, so this one route can't itself be attendance-gated.
  const resolvingPastDayEnd =
    location.pathname === "/tada" && new URLSearchParams(location.search).has("date");

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#f7f5f9]">
        <div
          data-testid="auth-loading-spinner"
          className="h-10 w-10 rounded-full border-4 border-[#E5197F]/30 border-t-[#E5197F] animate-spin"
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requireChangedPassword && user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // Phase 29.1 — Attendance-First: a gated role (see `strictRoles`) who
  // hasn't punched in today gets redirected to the attendance screen.
  if (!attendancePunchedIn && location.pathname !== "/attendance" && !resolvingPastDayEnd) {
    return <Navigate to="/attendance" replace />;
  }

  return children;
}
